import { pushDataLayer } from './dataLayer';
import { ECOMMERCE_CURRENCY_CODE, ECOMMERCE_PRODUCT_CLICK_EVENT } from '../config';
import type {
  EcommerceImpressionsEvent,
  EcommerceItemSource,
  EcommerceProduct,
  EcommerceProductClickEvent,
  EcommerceProductSource,
  EcommerceTouristGroup,
} from '../model/ecommerce';

const ECOMMERCE_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})/;

export function formatEcommerceDate(date: string): string {
  const match = ECOMMERCE_DATE_PATTERN.exec(date);

  if (!match) return '';

  const [, year, month, day] = match;

  return `${day}_${month}_${year}`;
}

export function getEcommerceTourists(roomGroups: EcommerceProductSource['roomGroups']): EcommerceTouristGroup {
  return roomGroups.reduce<EcommerceTouristGroup>(
    (total, { touristGroup }) => ({
      adults: total.adults + touristGroup.adults,
      kids: total.kids + touristGroup.kids,
      infants: total.infants + touristGroup.infants,
    }),
    { adults: 0, kids: 0, infants: 0 },
  );
}

function getEcommerceCategory(product: EcommerceProductSource, departureCityName: string): string {
  const countryName = product.country?.name ?? product.resort?.countryName ?? '';
  const resortName = product.resort?.name ?? '';
  const hotelCategoryName = product.hotelCategory?.name ?? '';
  const mealName = product.meal?.name ?? '';

  return `${departureCityName}/${countryName}/${resortName}/${hotelCategoryName}/${product.roomName}|${mealName}`;
}

function getEcommerceVariant(product: EcommerceProductSource): string {
  const { adults, kids, infants } = getEcommerceTourists(product.roomGroups);

  return `${product.nights.total}|${formatEcommerceDate(product.checkInDate)}|${adults}_${kids}_${infants}`;
}

export function getEcommerceProduct({
  hotel,
  product,
  departureCityName,
  position,
}: EcommerceItemSource): EcommerceProduct {
  return {
    id: product.id,
    name: `${hotel.name}|${hotel.id}`,
    category: getEcommerceCategory(product, departureCityName),
    brand: product.operator?.name ?? '',
    variant: getEcommerceVariant(product),
    price: product.price,
    position,
  };
}

export function getEcommerceImpressionsData(
  items: readonly EcommerceItemSource[],
  list: string,
): EcommerceImpressionsEvent | null {
  if (!items.length) return null;

  return {
    ecommerce: {
      currencyCode: ECOMMERCE_CURRENCY_CODE,
      impressions: items.map((item) => ({ ...getEcommerceProduct(item), list })),
    },
  };
}

export function getEcommerceProductClickData(item: EcommerceItemSource, list: string): EcommerceProductClickEvent {
  return {
    event: ECOMMERCE_PRODUCT_CLICK_EVENT,
    ecommerce: {
      click: {
        actionField: { list },
        products: [getEcommerceProduct(item)],
      },
    },
  };
}

export function trackEcommerceImpressions(items: readonly EcommerceItemSource[], list: string): boolean {
  const data = getEcommerceImpressionsData(items, list);

  return data ? pushDataLayer(data) : false;
}

export function trackEcommerceProductClick(item: EcommerceItemSource, list: string): boolean {
  return pushDataLayer(getEcommerceProductClickData(item, list));
}
